// src/lib/managers/agenda/AgendaManager.ts

import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { parseGraphDateTimeToIsoString } from "../../utils/date/format-time";
import { DataManager } from "../dataManager/DataManager";
import { isThrottleError } from "../dataManager/graph/GraphThrottleError";
import { AgendaEventsPage } from "../dataManager/IDataApi";
// import { GraphManager } from "../dataManager/graph";
import {
    AttendeeResponseStatus,
    EventMessageType,
    LinkedEventDetails,
    MailManager,
} from "../mail";
import {
    computeFreeSlots,
    DEFAULT_SLOT_MINUTES,
    parseFreeSlotsRange,
} from "./freeSlots";
import {
    AgendaFilter,
    AgendaItem,
    AgendaPage,
    AgendaTimeSlot,
    CreateAgendaItemData,
    UpdateAgendaItemData,
} from "./types/agenda";

/**
 * @class AgendaManager
 * Gère les opérations liées au calendrier et aux événements
 * ═══════════════════════════════════════════════════════════════════════════
 */
export class AgendaManager {
    // #region Properties

    private eventBus: EventBus;
    // private graphManager?: GraphManager;
    private dataManager: DataManager;
    private mailManager: MailManager;

    private pollingInterval: ReturnType<typeof setTimeout> | null = null;
    private pollingDelayInMs = 20000; // 20 secondes
    private lastAgendaPollingDate: Date | null = null;

    private hash: number = Math.round(Math.random() * 100);

    private isInitialisationDone = false;
    private isLicenseValid = false;

    // #endregion Properties

    /**
     * @constructor
     * Initialise un nouveau AgendaManager avec les dépendances nécessaires
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {EventBus} eventBus - Bus d'événements pour la communication
     * @input {DataManager} dataManager - Gestionnaire Data API
     * @input {MailManager} mailManager - Gestionnaire des emails
     * @output {void}
     */
    constructor(
        eventBus: EventBus,
        dataManager: DataManager,
        mailManager: MailManager,
    ) {
        logger.info([LOGS.AgendaManager], `AgendaManager(${this.hash})`);

        this.eventBus = eventBus;
        this.dataManager = dataManager;
        this.mailManager = mailManager;
    }

    // #region Initialisation and cleanup

    /**
     * Do nothing
     */
    public async _initialize(): Promise<void> {
        logger.info(
            [LOGS.AgendaManager, LOGS.ServiceProvider],
            `AgendaManager.initialize()`,
        );

        this.isInitialisationDone = true;
    }

    /**
     * Starts Agenda polling
     */
    public async _startUp(): Promise<void> {
        logger.info(
            [LOGS.AgendaManager, LOGS.ServiceProvider],
            `AgendaManager._startup()`,
        );

        if (this.isLicenseValid) {
            this._startAgendaPolling();
        }
    }

    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the AgendaManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.AgendaManager,
            "AgendaManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.AgendaManager,
                "  Phase 1: Stopping active processes",
            );

            // Stop agenda polling if active
            logger.info(LOGS.AgendaManager, "     Stop agenda polling");
            this._stopAgendaPolling();
        } catch (error) {
            logger.error(
                LOGS.AgendaManager,
                `AgendaManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        // Phase 2: Clear internal state and caches
        try {
            logger.info(
                LOGS.AgendaManager,
                "  Phase 2: Clearing internal state",
            );

            // Reset polling settings
            this.pollingDelayInMs = 20000;
        } catch (error) {
            logger.error(
                LOGS.AgendaManager,
                `AgendaManager.cleanup() - Error in Phase 2: ${error}`,
            );
        }

        logger.info(
            LOGS.AgendaManager,
            "AgendaManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Event Handlers

    public _registerEvents() {
        this["user-license-updated_handler"] =
            this["user-license-updated_handler"].bind(this);

        // Enregistre le statut de license de l'utilisateur
        this.eventBus.on(
            "user-license-updated",
            this["user-license-updated_handler"],
        );
    }

    private async "user-license-updated_handler"(
        licenseIsValid: boolean,
    ): Promise<void> {
        logger.info(
            LOGS.AgendaManager,
            `AgendaManager (${this.hash}) - processes user-license-updated_handler`,
        );

        // Manage license update after Initialization done
        if (this.isInitialisationDone) {
            // If license if deactivated, stops polling
            if (this.isLicenseValid == !licenseIsValid) {
                logger.info(
                    LOGS.AgendaManager,
                    "Stopping polling after license deactivated event received",
                );
                this._stopAgendaPolling();
            }
            // If license is activated, starts polling
            else if (!this.isLicenseValid == licenseIsValid) {
                logger.info(
                    LOGS.AgendaManager,
                    "Starting polling after license activated event received",
                );
                this._startAgendaPolling();
            }
        }

        // In any case, update internal backup of license state
        this.isLicenseValid = licenseIsValid;
    }

    // #endregion Event Handlers

    // #region Public Methods

    /**
     * @method getUserAgenda
     * Récupère les événements du calendrier de l'utilisateur pour une période donnée
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} startDate - Date de début au format ISO
     * @input {string} endDate - Date de fin au format ISO
     * @output {Promise<AgendaItem[]>} Liste des événements du calendrier
     */
    public async getUserAgenda(
        startDate: string,
        endDate: string,
    ): Promise<AgendaItem[]> {
        const startTime = performance.now();
        logger.info(
            [LOGS.AgendaManager],
            `getUserAgenda(${startDate}, ${endDate})`,
        );

        let agendaItems: AgendaItem[] = [];

        try {
            let agendaFilter: AgendaFilter = {
                startDate: startDate,
                endDate: endDate,
            };
            // Appel à l'API Graph via DataManager
            let graphEvents = [];
            try {
                graphEvents =
                    await this.dataManager.getUserAgenda(agendaFilter);
            } catch (error) {
                // Filet « ceinture-bretelles » NON-429 : le throttle 429 est déjà géré
                // exhaustivement plus bas (callGraphEndPoint). Ne pas rejouer dessus.
                if (isThrottleError(error)) throw error;
                logger.error(
                    [LOGS.AgendaManager],
                    `Error in getUserAgenda during getUserAgenda : ${error}`,
                );
                logger.warn(
                    LOGS.AgendaManager,
                    `  Retrying once after 2s delay ...`,
                );

                await new Promise((resolve) => setTimeout(resolve, 2000));
                graphEvents =
                    await this.dataManager.getUserAgenda(agendaFilter);
                logger.warn(LOGS.AgendaManager, `  Retry succeeded`);
            }

            if (!graphEvents || !Array.isArray(graphEvents)) {
                logger.error(
                    [LOGS.AgendaManager],
                    "Invalid response from DataManager.getUserAgenda",
                );
                return [];
            }

            // Transformation des événements Graph en AgendaItems
            agendaItems = graphEvents.map((event) =>
                this.mapGraphEventToAgendaItem(event),
            );

            logger.info(
                [LOGS.AgendaManager],
                `Retrieved ${agendaItems.length} agenda items`,
            );
            logger.info([LOGS.AgendaManager], "Agenda Items", agendaItems);

            // Initialiser la date de dernier polling après récupération
            this.lastAgendaPollingDate = new Date();
            logger.info(
                [LOGS.AgendaManager],
                `Initialized lastAgendaPollingDate: ${this.lastAgendaPollingDate.toISOString()}`,
            );
        } catch (error) {
            logger.error(
                [LOGS.AgendaManager],
                `Error in getUserAgenda: ${error}`,
            );
            throw error;
        }

        logger.perf([LOGS.Perfs], `AgendaManager - getUserAgenda`, startTime);

        return agendaItems;
    }

    /**
     * @method getUserAgendaPage
     * Paginated agenda retrieval (AB#158). Fetches one provider page, maps Graph events to
     * AgendaItems, and applies the client-side attendee filter (Graph/Google cannot filter the
     * `attendees` collection server-side). Returns the mapped items + the opaque cursor to continue.
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {AgendaFilter} filter - window (startDate/endDate), orderBy, limit (= page size), attendees, keywords
     * @input {string} [cursor] - opaque cursor from a previous page (undefined = first page)
     * @input {{ includeBody?: boolean }} [options] - false → light scan (no `body`, hydrated later)
     * @output {Promise<AgendaPage>} mapped items + nextCursor
     */
    public async getUserAgendaPage(
        filter: AgendaFilter,
        cursor?: string,
        options?: { includeBody?: boolean },
    ): Promise<AgendaPage> {
        const startTime = performance.now();
        logger.info(
            [LOGS.AgendaManager],
            `getUserAgendaPage(cursor: ${cursor ? "yes" : "no"}, includeBody: ${options?.includeBody ?? true})`,
        );

        let page: AgendaEventsPage = { events: [] };
        try {
            page = await this.dataManager.getUserAgendaPage(
                filter,
                cursor,
                options,
            );
        } catch (error) {
            // Belt-and-suspenders (NON-429 only; 429 is handled centrally in callGraphEndPoint).
            if (isThrottleError(error)) throw error;
            logger.error(
                [LOGS.AgendaManager],
                `Error in getUserAgendaPage during dataManager call : ${error}`,
            );
            logger.warn(
                LOGS.AgendaManager,
                `  Retrying once after 2s delay ...`,
            );
            await new Promise((resolve) => setTimeout(resolve, 2000));
            page = await this.dataManager.getUserAgendaPage(
                filter,
                cursor,
                options,
            );
            logger.warn(LOGS.AgendaManager, `  Retry succeeded`);
        }

        const events = Array.isArray(page?.events) ? page.events : [];
        let items = events.map((event) =>
            this.mapGraphEventToAgendaItem(event),
        );

        // Client-side attendee filter (server-side filtering of the `attendees` collection is unsupported).
        if (filter.attendees && filter.attendees.length > 0) {
            const needles = filter.attendees.map((a) => a.toLowerCase());
            items = items.filter((item) =>
                this._matchesAttendees(item, needles),
            );
        }

        logger.perf(
            [LOGS.Perfs],
            `AgendaManager - getUserAgendaPage`,
            startTime,
        );
        return { items, nextCursor: page?.nextCursor };
    }

    /**
     * @method _matchesAttendees
     * True if the event involves any of the given (lowercased) attendee needles — matched on an
     * attendee address/name substring, or on the organizer address (exact). Mirrors the previous
     * client-side attendee filter of getUserAgenda.
     * @private
     */
    private _matchesAttendees(item: AgendaItem, needles: string[]): boolean {
        return needles.some(
            (needle) =>
                item.attendees?.some(
                    (a) =>
                        a.emailAddress?.address
                            ?.toLowerCase()
                            .includes(needle) ||
                        a.emailAddress?.name?.toLowerCase().includes(needle),
                ) ||
                item.organizer?.emailAddress?.address?.toLowerCase() === needle,
        );
    }

    /**
     * @method hydrateBodies
     * Fills the full `body` (+ bodyPreview fallback) for the given items that lack it — used after a
     * light scan so only the displayed events pay the body payload. Best-effort: a body-fetch failure
     * (or an unresolved recurring-occurrence id) leaves the item on its scan `bodyPreview`.
     * @input {AgendaItem[]} items - items to hydrate in place
     */
    public async hydrateBodies(items: AgendaItem[]): Promise<void> {
        const idsToHydrate = items
            .filter((item) => !item.body && item.id)
            .map((item) => item.id);
        if (idsToHydrate.length === 0) return;

        try {
            const bodies =
                await this.dataManager.getAgendaItemBodies(idsToHydrate);
            for (const item of items) {
                const hydrated = bodies[item.id];
                if (!hydrated) continue;
                if (hydrated.body) item.body = hydrated.body;
                if (!item.bodyPreview && hydrated.bodyPreview)
                    item.bodyPreview = hydrated.bodyPreview;
            }
        } catch (error) {
            if (isThrottleError(error)) throw error;
            // Best-effort: cards gracefully fall back to bodyPreview if hydration fails.
            logger.error(
                [LOGS.AgendaManager],
                `Error in hydrateBodies: ${error}`,
            );
        }
    }

    /**
     * @method createAgendaItem
     * Crée un nouvel événement dans le calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {CreateAgendaItemData} agendaItemData - Données de l'événement à créer
     * @output {Promise<boolean>} Succès de la création
     */
    public async createAgendaItem(
        agendaItemData: CreateAgendaItemData,
    ): Promise<boolean> {
        logger.info(
            [LOGS.AgendaManager],
            `createAgendaItem(${agendaItemData.subject})`,
        );

        // Check input params
        if (
            !agendaItemData.subject ||
            !agendaItemData.start ||
            !agendaItemData.end
        ) {
            logger.error(
                [LOGS.AgendaManager],
                "Missing required Agenda Item data (subject, start, end)",
            );
            throw new Error(
                "Missing required Agenda Item data: subject, start, and end are required",
            );
        }

        try {
            // Appel à l'API Graph via DataManager
            const success =
                await this.dataManager.createAgendaItem(agendaItemData);

            if (success) {
                logger.info(
                    [LOGS.AgendaManager],
                    `Agenda Item "${agendaItemData.subject}" created successfully`,
                );

                // TODO : Émettre un événement pour notifier la création
            } else {
                logger.error(
                    [LOGS.AgendaManager],
                    `Failed to create Agenda Item "${agendaItemData.subject}"`,
                );
            }

            return success;
        } catch (error) {
            logger.error(
                [LOGS.AgendaManager],
                `Error in createAgendaItem: ${error}`,
            );
            throw error;
        }
    }

    /**
     * @method findFreeTimeSlots
     * Trouve les créneaux libres dans l'agenda de l'utilisateur pour une période donnée
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} startDate - Date de début de la recherche (format ISO)
     * @input {string} endDate - Date de fin de la recherche (format ISO)
     * @input {number} minutesToAllocate - Durée minimum des créneaux recherchés en minutes ; absente
     * (facultative dans l'outil de l'Api-Ai), `DEFAULT_SLOT_MINUTES`
     * @output {Promise<AgendaTimeSlot[]>} Liste des créneaux libres disponibles (voir `computeFreeSlots`)
     * @throws {Error} Si une date est illisible, si le début n'est pas avant la fin, ou si l'agenda
     * n'a pas pu être lu
     */
    public async findFreeTimeSlots(
        startDate: string,
        endDate: string,
        minutesToAllocate: number,
    ): Promise<AgendaTimeSlot[]> {
        logger.info(
            [LOGS.AgendaManager],
            `findFreeTimeSlots(${startDate}, ${endDate}, ${minutesToAllocate})`,
        );

        // Validation des paramètres
        // Durée absente : celle que l'outil annonce par défaut, plutôt qu'aucun créneau.
        minutesToAllocate = minutesToAllocate ?? DEFAULT_SLOT_MINUTES;

        try {
            // Dates et durée vérifiées avant de lire l'agenda : une date illisible donne une erreur
            // explicite, et non une liste vide ou un appel au fournisseur (F-1.10).
            parseFreeSlotsRange(startDate, endDate, minutesToAllocate);

            // Récupération de tous les événements de la période
            const agendaItems = await this.getUserAgenda(startDate, endDate);
            logger.info(
                [LOGS.AgendaManager],
                `Retrieved ${agendaItems.length} agenda items`,
            );

            // Jours calendaires, chacun borné à [max(7h, début, maintenant), min(19h, fin)]
            const freeSlots = computeFreeSlots(
                agendaItems,
                startDate,
                endDate,
                new Date(),
                minutesToAllocate,
            );

            logger.info(
                [LOGS.AgendaManager],
                `Found ${freeSlots.length} free slots of ${minutesToAllocate}+ minutes`,
            );
            return freeSlots;
        } catch (error) {
            logger.error(
                [LOGS.AgendaManager],
                `Error in findFreeTimeSlots: ${error}`,
            );
            throw error;
        }
    }

    /**
     * @method updateAgendaItem
     * Met à jour un événement existant dans le calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à mettre à jour
     * @input {UpdateAgendaItemData} updateData - Données de l'événement à modifier
     * @output {Promise<boolean>} Succès de la mise à jour
     * @throws {Error} Si itemId manque
     */
    public async updateAgendaItem(
        itemId: string,
        updateData: UpdateAgendaItemData,
    ): Promise<boolean> {
        logger.info([LOGS.AgendaManager], `updateAgendaItem(${itemId})`);

        // Check input params
        if (!itemId || itemId.trim() === "") {
            logger.error(
                [LOGS.AgendaManager],
                "Missing required itemId parameter",
            );
            throw new Error("itemId is required for updating an agenda item");
        }

        // Validation des données temporelles si présentes
        if (updateData.timeUpdate) {
            if (!updateData.timeUpdate.start || !updateData.timeUpdate.end) {
                logger.error(
                    [LOGS.AgendaManager],
                    "Both start and end are required when updating time",
                );
                throw new Error(
                    "Both start and end dates are required when updating event time",
                );
            }

            // Vérifier que la date de fin est après la date de début
            const startDate = new Date(updateData.timeUpdate.start);
            const endDate = new Date(updateData.timeUpdate.end);

            if (endDate <= startDate) {
                logger.error(
                    [LOGS.AgendaManager],
                    "End date must be after start date",
                );
                throw new Error("End date must be after start date");
            }
        }

        try {
            // Appel à l'API Graph via DataManager
            const success = await this.dataManager.updateAgendaItem(
                itemId,
                updateData,
            );

            if (success) {
                logger.info(
                    [LOGS.AgendaManager],
                    `Agenda Item "${itemId}" updated successfully`,
                );

                // TODO : Émettre un événement pour notifier la mise à jour
                // this.eventBus.emit('agenda-item-updated', { itemId, updateData });
            } else {
                logger.error(
                    [LOGS.AgendaManager],
                    `Failed to update Agenda Item "${itemId}"`,
                );
            }

            return success;
        } catch (error) {
            logger.error(
                [LOGS.AgendaManager],
                `Error in updateAgendaItem: ${error}`,
            );
            throw error;
        }
    }

    /**
     * @method deleteAgendaItem
     * Supprime un événement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à supprimer
     * @output {Promise<boolean>} Succès de la suppression
     * @throws {Error} Si itemId manque
     */
    public async deleteAgendaItem(itemId: string): Promise<boolean> {
        logger.info([LOGS.AgendaManager], `deleteAgendaItem(${itemId})`);

        // Check input params
        if (!itemId || itemId.trim() === "") {
            logger.error(
                [LOGS.AgendaManager],
                "Missing required itemId parameter",
            );
            throw new Error("itemId is required for deleting an agenda item");
        }

        try {
            // Appel à l'API Graph via DataManager
            const success = await this.dataManager.deleteAgendaItem(itemId);

            if (success) {
                logger.info(
                    [LOGS.AgendaManager],
                    `Agenda Item "${itemId}" deleted successfully`,
                );

                // TODO : Émettre un événement pour notifier la suppression
                // this.eventBus.emit('agenda-item-deleted', itemId);
            } else {
                logger.error(
                    [LOGS.AgendaManager],
                    `Failed to delete Agenda Item "${itemId}"`,
                );
            }

            return success;
        } catch (error) {
            logger.error(
                [LOGS.AgendaManager],
                `Error in deleteAgendaItem: ${error}`,
            );
            throw error;
        }
    }

    /**
     * Accepte un évènement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à accepter
     * @output {Promise<boolean>} Succès de l'acceptation
     * @throws {Error} Si itemId manque
     */
    public async acceptAgendaItem(itemId: string): Promise<boolean> {
        logger.info([LOGS.AgendaManager], `acceptAgendaItem(${itemId})`);

        // Check input params
        if (!itemId || itemId.trim() === "") {
            logger.error(
                [LOGS.AgendaManager],
                "Missing required itemId parameter",
            );
            throw new Error("itemId is required for deleting an agenda item");
        }

        try {
            // Appel à l'API graph via DataManager
            const success = await this.dataManager.acceptAgendaItem(itemId);

            if (success) {
                logger.info(
                    [LOGS.AgendaManager],
                    `Agenda Item ${itemId} accepted successfully`,
                );
            } else {
                logger.error(
                    [LOGS.AgendaManager],
                    `Failed to accept Agenda Item: ${itemId}`,
                );
            }

            return success;
        } catch (error) {
            logger.error(
                [LOGS.AgendaManager],
                `Error in acceptAgendaItem: ${error}`,
            );
            throw error;
        }
    }

    /**
     * Accepte provisoirement un évènement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à accepter provisoirement
     * @output {Promise<boolean>} Succès de l'acceptation provisoire
     * @throws {Error} Si itemId manque
     */
    public async tentativelyAcceptAgendaItem(itemId: string): Promise<boolean> {
        logger.info(
            [LOGS.AgendaManager],
            `tentativelyAcceptAgendaItem(${itemId})`,
        );

        // Check input params
        if (!itemId || itemId.trim() === "") {
            logger.error(
                [LOGS.AgendaManager],
                "Missing required itemId parameter",
            );
            throw new Error("itemId is required for deleting an agenda item");
        }

        try {
            // Appel à l'API graph via DataManager
            const success =
                await this.dataManager.tentativelyAcceptAgendaItem(itemId);

            if (success) {
                logger.info(
                    [LOGS.AgendaManager],
                    `Agenda Item ${itemId} accepted tentatively successfully`,
                );
            } else {
                logger.error(
                    [LOGS.AgendaManager],
                    `Failed to accept tentatively Agenda Item: ${itemId}`,
                );
            }

            return success;
        } catch (error) {
            logger.error(
                [LOGS.AgendaManager],
                `Error in tentativelyAcceptAgendaItem: ${error}`,
            );
            throw error;
        }
    }

    /**
     * Refuse un évènement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à refuser
     * @output {Promise<boolean>} Succès de l'acceptation
     * @throws {Error} Si itemId manque
     */
    public async declineAgendaItem(itemId: string): Promise<boolean> {
        logger.info([LOGS.AgendaManager], `declineAgendaItem(${itemId})`);

        // Check input params
        if (!itemId || itemId.trim() === "") {
            logger.error(
                [LOGS.AgendaManager],
                "Missing required itemId parameter",
            );
            throw new Error("itemId is required for deleting an agenda item");
        }

        try {
            // Appel à l'API graph via DataManager
            const success = await this.dataManager.declineAgendaItem(itemId);

            if (success) {
                logger.info(
                    [LOGS.AgendaManager],
                    `Agenda Item ${itemId} declined successfully`,
                );
            } else {
                logger.error(
                    [LOGS.AgendaManager],
                    `Failed to decline Agenda Item: ${itemId}`,
                );
            }

            return success;
        } catch (error) {
            logger.error(
                [LOGS.AgendaManager],
                `Error in declineAgendaItem: ${error}`,
            );
            throw error;
        }
    }

    // #endregion Public Methods

    // #region Private Methods

    /**
     * @method pollForNewAgendaItems
     * Vérifie périodiquement la présence de nouveaux événements d'agenda
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @private
     */
    private async pollForNewAgendaItems(): Promise<void> {
        logger.info([LOGS.AgendaManager], "Polling for new agenda items");

        try {
            if (!this.lastAgendaPollingDate) {
                logger.info(
                    [LOGS.AgendaManager],
                    `No lastAgendaPollingDate set, skipping polling`,
                );
                return;
            }

            // Récupérer les événements modifiés depuis le dernier polling
            logger.info(
                [LOGS.AgendaManager],
                `Polling events since: ${this.lastAgendaPollingDate.toISOString()}`,
            );

            // Appel Graph avec filtre sur date de modification
            let graphEvents = [];
            try {
                graphEvents = await this.dataManager.getUserAgendaModifiedSince(
                    this.lastAgendaPollingDate.toISOString(),
                );
            } catch (error) {
                // Filet « ceinture-bretelles » NON-429 : sur un 429, on laisse remonter
                // (avalé par le catch du polling) — le prochain tick réessaiera, sans hammering.
                if (isThrottleError(error)) throw error;
                logger.error(
                    [LOGS.AgendaManager],
                    `Error in pollForNewAgendaItems during getUserAgendaModifiedSince : ${error}`,
                );
                logger.warn(
                    LOGS.AgendaManager,
                    `  Retrying once after 2s delay ...`,
                );

                await new Promise((resolve) => setTimeout(resolve, 2000));
                graphEvents = await this.dataManager.getUserAgendaModifiedSince(
                    this.lastAgendaPollingDate.toISOString(),
                );
                logger.warn(LOGS.AgendaManager, `  Retry succeeded`);
            }

            if (!graphEvents || graphEvents.length === 0) {
                logger.info(
                    [LOGS.AgendaManager],
                    `Polling received no new agenda events`,
                );
                return;
            }

            logger.info(
                [LOGS.AgendaManager],
                `Polling received ${graphEvents.length} agenda events`,
            );

            // Transformation des événements Graph en AgendaItems
            const agendaItems = graphEvents.map((event) =>
                this.mapGraphEventToAgendaItem(event),
            );

            // Transformation des événements Graph en LinkedEventDetails pour le MailManager
            const linkedEventDetails = graphEvents.map((event) =>
                this._mapGraphEventToLinkedEventDetails(event),
            );

            // Séparation entre nouveaux et modifiés
            const newItems: AgendaItem[] = [];
            const updatedItems: AgendaItem[] = [];

            agendaItems.forEach((item) => {
                const createdDate = new Date(item.createdDateTime);
                const modifiedDate = new Date(item.lastModifiedDateTime);

                // Si created et modified sont identiques (ou très proches), c'est un nouvel élément
                const timeDiff = Math.abs(
                    modifiedDate.getTime() - createdDate.getTime(),
                );
                const isNew = timeDiff < 60000; // Moins d'1 minute de différence

                if (isNew) {
                    newItems.push(item);
                } else {
                    updatedItems.push(item);
                }
            });

            logger.info(
                [LOGS.AgendaManager],
                `Found ${newItems.length} new items and ${updatedItems.length} updated items`,
            );

            // Émettre les événements pour les éléments modifiés
            if (updatedItems.length > 0) {
                updatedItems.forEach((item) => {
                    this.eventBus.emit("agenda-item-updated", item);
                });
            }

            // Émettre l'événement pour les nouveaux éléments
            if (newItems.length > 0) {
                newItems.forEach((item) => {
                    this.eventBus.emit("agenda-item-new-item", item);
                });
            }

            // Mettre à jour les mails existants avec les nouveaux événements d'agenda
            if (linkedEventDetails.length > 0) {
                await this.mailManager.updateMailsWithLinkedEventDetails(
                    linkedEventDetails,
                );
            }

            // Mettre à jour la date de dernier polling
            this.lastAgendaPollingDate = new Date();
            logger.info(
                [LOGS.AgendaManager],
                `Updated lastAgendaPollingDate: ${this.lastAgendaPollingDate.toISOString()}`,
            );
        } catch (error) {
            logger.error(
                [LOGS.AgendaManager],
                `Error polling for new agenda items: ${error}`,
            );
        }
    }

    /**
     * @method startAgendaPolling
     * Démarre le polling périodique des événements d'agenda
     * ═══════════════════════════════════════════════════════════════════════════
     * @private
     */
    private _startAgendaPolling(): void {
        if (!this.pollingInterval) {
            // Arm the polling baseline when it has never been set. pollForNewAgendaItems() self-skips
            // while lastAgendaPollingDate is null, and the paginated assistant path (getUserAgendaPage)
            // — unlike getUserAgenda — never sets it. Without this, an "assistant only" session would
            // never arm polling and would miss every external invite/change (AB#158). We only bootstrap
            // when null, so a baseline already set by getUserAgenda / a prior poll (a possibly older,
            // more complete "modified since") is preserved across a stop/restart.
            if (!this.lastAgendaPollingDate) {
                this.lastAgendaPollingDate = new Date();
                logger.info(
                    [LOGS.AgendaManager],
                    `Armed lastAgendaPollingDate at polling start: ${this.lastAgendaPollingDate.toISOString()}`,
                );
            }
            this.pollingInterval = setInterval(
                () => this.pollForNewAgendaItems(),
                this.pollingDelayInMs,
            );
            logger.info([LOGS.AgendaManager], "Agenda polling started");
        }
    }

    /**
     * @method stopAgendaPolling
     * Arrête le polling périodique des événements d'agenda
     * ═══════════════════════════════════════════════════════════════════════════
     * @private
     */
    private _stopAgendaPolling(): void {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
            logger.info([LOGS.AgendaManager], "Agenda polling stopped");
        }
    }

    /**
     * @method mapGraphEventToAgendaItem
     * Transforme un événement Graph en AgendaItem
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {any} graphEvent - Événement depuis l'API Graph
     * @output {AgendaItem} Événement transformé
     * @private
     */
    private mapGraphEventToAgendaItem(graphEvent: any): AgendaItem {
        return {
            id: graphEvent.id || "",
            subject: graphEvent.subject || "",
            bodyPreview: graphEvent.bodyPreview || "",
            // Optional chaining: the light scan projection ($select without `body`) omits `body`;
            // it is hydrated afterwards for the displayed items via hydrateBodies().
            body: graphEvent.body?.content || null,
            start: {
                dateTime: graphEvent.start?.dateTime || "",
                timeZone:
                    graphEvent.start?.timeZone ||
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            end: {
                dateTime: graphEvent.end?.dateTime || "",
                timeZone:
                    graphEvent.end?.timeZone ||
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            location: graphEvent.location
                ? {
                      displayName: graphEvent.location.displayName || "",
                      locationType: graphEvent.location.locationType || "",
                      uniqueIdType: graphEvent.location.uniqueIdType || "",
                  }
                : undefined,
            attendees: graphEvent.attendees
                ? graphEvent.attendees.map((attendee: any) => ({
                      emailAddress: {
                          name: attendee.emailAddress?.name || "",
                          address: attendee.emailAddress?.address || "",
                      },
                      status: {
                          response: attendee.status?.response || "none",
                          time: attendee.status?.time || "",
                      },
                  }))
                : [],
            organizer: graphEvent.organizer
                ? {
                      emailAddress: {
                          name: graphEvent.organizer.emailAddress?.name || "",
                          address:
                              graphEvent.organizer.emailAddress?.address || "",
                      },
                  }
                : undefined,
            categories: graphEvent.categories || [],
            onlineMeeting: graphEvent.onlineMeeting
                ? {
                      joinUrl: graphEvent.onlineMeeting.joinUrl || "",
                      conferenceId: graphEvent.onlineMeeting.conferenceId || "",
                      tollNumber: graphEvent.onlineMeeting.tollNumber || "",
                  }
                : undefined,
            isAllDay: graphEvent.isAllDay || false,
            isCancelled: graphEvent.isCancelled || false,
            importance: graphEvent.importance || "normal",
            sensitivity: graphEvent.sensitivity || "normal",
            showAs: graphEvent.showAs || "busy",
            type: graphEvent.type || "singleInstance",
            webLink: graphEvent.webLink || "",
            createdDateTime: graphEvent.createdDateTime || "",
            lastModifiedDateTime: graphEvent.lastModifiedDateTime || "",
            // Réponse de l'utilisateur à l'invitation : une réunion refusée ne bloque pas ses
            // heures dans les créneaux libres (F-1.12). Absente si le fournisseur ne la donne pas.
            ...(graphEvent.responseStatus?.response && {
                responseStatus: {
                    response: graphEvent.responseStatus.response,
                    time: graphEvent.responseStatus.time || "",
                },
            }),
        };
    }

    /**
     * @method mapGraphEventToLinkedEventDetails
     * Transforme un événement Graph en LinkedEventDetails
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {any} graphEvent - Événement depuis l'API Graph
     * @output {LinkedEventDetails} Détails d'événement lié transformé
     * @private
     */
    private _mapGraphEventToLinkedEventDetails(
        graphEvent: any,
    ): LinkedEventDetails {
        return {
            id: graphEvent.id || "",
            meetingMessageType: graphEvent.meetingMessageType || "",
            meetingRequestType: graphEvent.meetingRequestType || "",
            subject: graphEvent.subject || "",
            bodyPreview: graphEvent.bodyPreview || "",
            startDateTime: parseGraphDateTimeToIsoString(graphEvent.start),
            endDateTime: parseGraphDateTimeToIsoString(graphEvent.end),
            isAllDay: graphEvent.isAllDay || false,
            recurrence: graphEvent.recurrence || null,
            responseRequested: graphEvent.responseRequested || false,
            isDelegated: graphEvent.isDelegated || false,
            location: graphEvent.location
                ? {
                      displayName: graphEvent.location.displayName || "",
                      locationType: graphEvent.location.locationType || "",
                      uniqueIdType: graphEvent.location.uniqueIdType || "",
                  }
                : {
                      displayName: "",
                      locationType: "",
                      uniqueIdType: "",
                  },
            userResponseStatus: this._mapResponseStatus(
                graphEvent.responseStatus?.response,
            ),
            isExpired: this._isEventExpired(graphEvent.end?.dateTime),
            isCancelled: graphEvent.isCancelled || false,
            messageType: this._mapEventMessageType(graphEvent.messageType),
            organizerMail: graphEvent.emailAddress?.address || "",
        };
    }

    /**
     * @method mapResponseStatus
     * Mappe le statut de réponse Graph vers AttendeeResponseStatus
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} graphResponseStatus - Statut de réponse depuis l'API Graph
     * @output {AttendeeResponseStatus} Statut de réponse normalisé
     * @private
     */
    private _mapResponseStatus(
        graphResponseStatus: string,
    ): AttendeeResponseStatus {
        switch (graphResponseStatus?.toLowerCase()) {
            case "accepted":
                return AttendeeResponseStatus.ACCEPTED;
            case "tentativelyaccepted":
            case "tentatively_accepted":
                return AttendeeResponseStatus.TENTATIVELY_ACCEPTED;
            case "declined":
                return AttendeeResponseStatus.DECLINED;
            case "organizer":
                return AttendeeResponseStatus.ORGANIZER;
            case "notresponded":
            case "not_responded":
                return AttendeeResponseStatus.NOT_RESPONDED;
            default:
                return AttendeeResponseStatus.NONE;
        }
    }

    /**
     * @method mapEventMessageType
     * Mappe le type de message Graph vers EventMessageType
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} graphMessageType - Type de message depuis l'API Graph
     * @output {EventMessageType} Type de message d'événement normalisé
     * @private
     */
    private _mapEventMessageType(graphMessageType: string): EventMessageType {
        switch (graphMessageType?.toLowerCase()) {
            case "request":
                return EventMessageType.EVENT_MESSAGE_REQUEST;
            case "response":
                return EventMessageType.EVENT_MESSAGE_RESPONSE;
            case "message":
            default:
                return EventMessageType.EVENT_MESSAGE;
        }
    }

    /**
     * @method isEventExpired
     * Vérifie si un événement est expiré
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} endDateTime - Date de fin de l'événement
     * @output {boolean} True si l'événement est expiré
     * @private
     */
    private _isEventExpired(endDateTime: string): boolean {
        if (!endDateTime) return false;
        const endDate = new Date(endDateTime);
        const now = new Date();
        return endDate < now;
    }
    // #endregion Private Methods
}
